import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './TrailerFinder.css';

interface FinderState {
  boatType: string;
  loadingPreference: string;
  waterType: string;
}

const TrailerFinder: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState<FinderState>({
    boatType: '',
    loadingPreference: '',
    waterType: ''
  });

  const questions = [
    {
      id: 'boatType',
      question: 'What type of boat do you have?',
      options: [
        { value: 'fishing', label: 'Fishing/Bass Boat', icon: '/images/icons/icon-bass-boat.png' },
        { value: 'bowrider', label: 'Bowrider', icon: '/images/icons/icon-bowrider.png' },
        { value: 'center-console', label: 'Center Console', icon: '/images/icons/icon-center-console.png' },
        { value: 'cruiser', label: 'Cruiser', icon: '/images/icons/icon-cruiser.png' },
        { value: 'pwc', label: 'Personal Watercraft', icon: '/images/icons/icon-jet-ski.png' },
        { value: 'skiff', label: 'Skiff/Jon Boat', icon: '/images/icons/icon-jon-boat.png' },
        { value: 'wakeboard', label: 'Wakeboard Boat', icon: '/images/icons/icon-wakeboard-boat.png' },
        { value: 'pontoon', label: 'Pontoon/Tritoon', icon: '/images/icons/icon-pontoon-baot.png' }
      ]
    },
    {
      id: 'loadingPreference',
      question: 'What type of loading do you prefer?',
      options: [
        { value: 'roller', label: 'Roller (Easy loading/unloading)', icon: '/images/icons/icon-trailer-roller.png' },
        { value: 'bunk', label: 'Bunk (Better support)', icon: '/images/icons/icon-trailer-bunk.png' }
      ]
    },
    {
      id: 'waterType',
      question: 'Where will you primarily use your boat?',
      options: [
        { value: 'saltwater', label: 'Saltwater (Ocean/Bay)', icon: '/images/icons/icon-saltwater.png' },
        { value: 'freshwater', label: 'Freshwater (Lake/River)', icon: '/images/icons/icon-freshwater.png' }
      ]
    }
  ];

  const handleAnswer = (questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    
    if (currentStep < questions.length) {
      setCurrentStep(currentStep + 1);
    } else {
      // Final step - calculate recommendation
      setTimeout(() => {
        const recommendation = getRecommendation(answers);
        navigate(recommendation);
      }, 500);
    }
  };

  const getRecommendation = (answers: FinderState): string => {
    // Specific boat type routing
    if (answers.boatType === 'pontoon') return '/trailers/pontoon#pontoon-trailers';
    if (answers.boatType === 'pwc') return '/trailers/pwc#pwc-trailers';
    if (answers.boatType === 'skiff') return '/trailers/skiff#skiff-trailers';
    
    // For saltwater use, prefer galvanized trailers for corrosion resistance
    if (answers.waterType === 'saltwater') {
      if (answers.loadingPreference === 'roller') {
        return '/trailers/roller#roller-trailers'; // Galvanized roller trailers
      } else {
        return '/trailers/bunk'; // Galvanized bunk trailers
      }
    }
    
    // For freshwater use, aluminum or galvanized based on boat type and preference
    if (answers.waterType === 'freshwater') {
      // For bowriders and fishing boats, prefer aluminum trailers for weight savings
      if ((answers.boatType === 'bowrider' || answers.boatType === 'fishing') && answers.loadingPreference === 'bunk') {
        return '/trailers/aluminum'; // Aluminum bunk trailers
      }
      
      if (answers.loadingPreference === 'roller') {
        return '/trailers/roller#roller-trailers'; // Roller trailers (aluminum or galvanized)
      } else {
        return '/trailers/bunk'; // Bunk trailers (aluminum or galvanized)
      }
    }

    // Fallback routing based on loading preference
    if (answers.loadingPreference === 'roller') return '/trailers/roller#roller-trailers';
    if (answers.loadingPreference === 'bunk') return '/trailers/bunk';

    // Default to general trailers
    return '/trailers';
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const resetFinder = () => {
    setCurrentStep(1);
    setAnswers({
      boatType: '',
      loadingPreference: '',
      waterType: ''
    });
  };

  const currentQuestion = questions[currentStep - 1];
  const progress = (currentStep / questions.length) * 100;

  return (
    <section className="trailer-finder">
      <div className="container">
        <div className="finder-header">
          <h2>Find Your Perfect Trailer</h2>
          <p>Answer a few quick questions and we'll recommend the ideal trailer for your needs</p>
        </div>

        <div className="finder-container">
          {/* Progress Bar */}
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            <span className="progress-text">Step {currentStep} of {questions.length}</span>
          </div>

          {/* Question */}
          <div className="question-container">
            <h3>{currentQuestion.question}</h3>
            
            <div className="options-grid">
              {currentQuestion.options.map((option) => (
                <button
                  key={option.value}
                  className="option-card"
                  onClick={() => handleAnswer(currentQuestion.id, option.value)}
                >
                  <img src={option.icon} alt={option.label} className="option-icon" />
                  <span className="option-label">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="finder-navigation">
            {currentStep > 1 && (
              <button className="nav-btn back-btn" onClick={goBack}>
                ← Back
              </button>
            )}
            <button className="nav-btn reset-btn" onClick={resetFinder}>
              Start Over
            </button>
          </div>
        </div>

        {/* Alternative CTA */}
        <div className="finder-alternative">
          <p>Need help deciding? <a href="/contact">Contact our experts</a> or <a href="/trailers">browse all trailers</a></p>
        </div>
      </div>
    </section>
  );
};

export default TrailerFinder;